import type { Candle } from '@tradekaro/shared';

interface Props {
  candles: Candle[];
  /** Epoch ms per candle, same order. Drives the x-axis labels. */
  times?: number[];
  /** true = label with HH:MM (PKT); false = label with the date. */
  intraday?: boolean;
  width?: number;
  height?: number;
}

const axisLabel = (ms: number | undefined, intraday: boolean): string => {
  if (!ms) return '';
  const d = new Date(ms);
  return intraday
    ? d.toLocaleTimeString('en-GB', { timeZone: 'Asia/Karachi', hour: '2-digit', minute: '2-digit', hour12: false })
    : d.toLocaleDateString('en-GB', { timeZone: 'Asia/Karachi', day: '2-digit', month: 'short', year: '2-digit' });
};

export const CandleChart = ({ candles, times, intraday = true, width = 880, height = 360 }: Props) => {
  if (!candles || candles.length === 0) return null;
  const pad = { top: 16, right: 56, bottom: 60, left: 8 };
  const W = width - pad.left - pad.right;
  const H = height - pad.top - pad.bottom;
  const volH = 50;
  const priceH = H - volH - 10;

  const all = candles.flatMap((c) => [c.hi, c.lo]);
  const min = Math.min(...all);
  const max = Math.max(...all);
  const range = max - min || 1;
  const maxVol = Math.max(...candles.map((c) => c.volume));

  const stepX = W / candles.length;
  const cw = Math.max(1.2, stepX * 0.6);

  const yPrice = (v: number) => pad.top + (1 - (v - min) / range) * priceH;
  const yVol = (v: number) => pad.top + priceH + 10 + (1 - v / maxVol) * volH;

  const yLabels = [0, 0.25, 0.5, 0.75, 1].map((p) => ({
    y: pad.top + p * priceH,
    v: max - p * range,
  }));

  // Labels come from the candles' own timestamps — never a fixed timetable.
  const xLabels = [0, 0.33, 0.66, 1].map((p) => {
    const idx = Math.min(candles.length - 1, Math.floor(p * (candles.length - 1)));
    return {
      x: pad.left + idx * stepX + cw / 2,
      t: axisLabel(times?.[idx], intraday),
    };
  });

  const lastClose = candles[candles.length - 1].close;
  const lastY = yPrice(lastClose);

  return (
    <svg
      width="100%"
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      style={{ display: 'block' }}
    >
      {yLabels.map((l, i) => (
        <g key={i}>
          <line
            x1={pad.left}
            x2={width - pad.right}
            y1={l.y}
            y2={l.y}
            stroke="var(--tk-line-2)"
            strokeWidth="1"
            strokeDasharray={i === 0 || i === 4 ? '' : '2 4'}
          />
          <text
            x={width - pad.right + 8}
            y={l.y + 3.5}
            fontSize="10.5"
            fontFamily="var(--tk-mono)"
            fill="var(--tk-ink-3)"
          >
            {l.v.toFixed(2)}
          </text>
        </g>
      ))}

      <line
        x1={pad.left}
        x2={width - pad.right}
        y1={lastY}
        y2={lastY}
        stroke="var(--tk-accent)"
        strokeWidth="1"
        strokeDasharray="3 3"
      />
      <rect
        x={width - pad.right + 2}
        y={lastY - 9}
        width={48}
        height="18"
        rx="3"
        fill="var(--tk-accent)"
      />
      <text
        x={width - pad.right + 26}
        y={lastY + 4}
        fontSize="10.5"
        fontFamily="var(--tk-mono)"
        fontWeight="600"
        fill="var(--tk-accent-ink)"
        textAnchor="middle"
      >
        {lastClose.toFixed(2)}
      </text>

      {candles.map((c, i) => {
        const x = pad.left + i * stepX + (stepX - cw) / 2;
        const cx = x + cw / 2;
        const isUp = c.close >= c.open;
        const color = isUp ? 'var(--tk-up)' : 'var(--tk-down)';
        const yO = yPrice(c.open);
        const yC = yPrice(c.close);
        const top = Math.min(yO, yC);
        const bH = Math.max(1, Math.abs(yC - yO));
        return (
          <g key={i}>
            <line
              x1={cx}
              x2={cx}
              y1={yPrice(c.hi)}
              y2={yPrice(c.lo)}
              stroke={color}
              strokeWidth="1"
            />
            <rect x={x} y={top} width={cw} height={bH} fill={color} />
            <rect
              x={x}
              y={yVol(c.volume)}
              width={cw}
              height={pad.top + priceH + 10 + volH - yVol(c.volume)}
              fill={color}
              opacity="0.35"
            />
          </g>
        );
      })}

      {xLabels.map((l, i) => (
        <text
          key={i}
          x={l.x}
          y={height - pad.bottom + 60 - 6}
          fontSize="10.5"
          fontFamily="var(--tk-mono)"
          fill="var(--tk-ink-3)"
          textAnchor="middle"
        >
          {l.t}
        </text>
      ))}

      <text
        x={pad.left}
        y={pad.top + priceH + 8}
        fontSize="9.5"
        fontFamily="var(--tk-mono)"
        fill="var(--tk-ink-3)"
        letterSpacing="0.04em"
      >
        VOLUME
      </text>
    </svg>
  );
};
